/* ==========================================================================
   SH Silver Collection – custom scripts for the Horizon theme
   --------------------------------------------------------------------------
   All store-specific JavaScript lives in this one file (loaded with `defer`
   from snippets/sh-body-end.liquid). Sections read their settings from
   data attributes, so no Liquid is needed here.

   Contents
   01  Shared helpers
   02  Header: mega menu featured image
   03  Homepage: offer banner slider        <sh-banner-slider>
   04  Product page: ring resize fee        #custom_size
   05  Product page: Tamara pay-later widgets
   06  Product page: frequently bought together bundle   <sh-product-bundle>
   07  Product page: Judge.me badge scrolls to reviews
   08  Recently viewed products             <sh-recently-viewed>
   09  Blog article: reading progress, product slider, copy link
   10  Footer: menus collapse into accordions on phones
   11  Scrolling rails with arrow buttons     [data-sh-rail] (homepage blog slider)
   12  Cart drawer: click the dimmed page to close it
   13  Removed product URLs (404) go to the all-products collection

   Store analytics tags (GA4, Google Ads) stay in snippets/sh-head.liquid and
   snippets/sh-body-end.liquid because they must run as separate tags.
   ========================================================================== */
(function () {
  'use strict';

  /* ==========================================================================
     01. Shared helpers
     ========================================================================== */

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // Formats cents with the shop's money format, e.g. "Dhs. {{amount}}".
  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  /* ==========================================================================
     02. Header: mega menu featured image follows the hovered category
     ========================================================================== */

  function onMegaHover(event) {
    var link = event.target.closest && event.target.closest('[data-sh-image]');
    if (!link) return;
    var mega = link.closest('[data-sh-mega]');
    if (!mega) return;
    var img = mega.querySelector('[data-sh-feature-image]');
    var caption = mega.querySelector('[data-sh-feature-caption]');
    var feature = mega.querySelector('[data-sh-feature-link]');
    if (!img || img.getAttribute('src') === link.dataset.shImage) return;
    img.removeAttribute('srcset');
    img.src = link.dataset.shImage;
    if (caption) caption.textContent = link.dataset.shCaption || '';
    if (feature) feature.href = link.href;
  }
  document.addEventListener('pointerover', onMegaHover);
  document.addEventListener('focusin', onMegaHover);

  /* ==========================================================================
     03. Homepage: offer banner slider
     ========================================================================== */

  // Native scroll-snap track with dots; autoplay pauses on hover/focus and
  // is off for reduced motion.
  define('sh-banner-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sh-banners__track');
      this.slides = [].slice.call(this.querySelectorAll('.sh-banners__slide'));
      this.dots = [].slice.call(this.querySelectorAll('.sh-banners__dot'));
      if (this.slides.length < 2) return;
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(+dot.dataset.index)));
      this.track.addEventListener('scroll', () => {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.sync(), 80);
      }, { passive: true });
      const delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      if (delay && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const start = () => { this.stop(); this.timer = setInterval(() => this.go((this.index() + 1) % this.slides.length), delay); };
        this.stop = () => clearInterval(this.timer);
        ['pointerenter', 'focusin'].forEach((e) => this.addEventListener(e, this.stop));
        ['pointerleave', 'focusout'].forEach((e) => this.addEventListener(e, start));
        start();
      } else {
        this.stop = () => {};
      }
    }
    // Positions come from the slides themselves so a partly scrolled or
    // resized track always lands exactly on a slide edge.
    index() {
      const x = this.track.scrollLeft;
      let best = 0;
      this.slides.forEach((s, n) => { if (Math.abs(s.offsetLeft - x) < Math.abs(this.slides[best].offsetLeft - x)) best = n; });
      return best;
    }
    go(i) { this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: 'smooth' }); }
    sync() { const i = this.index(); this.dots.forEach((d, n) => d.toggleAttribute('aria-current', n === i)); }
  });

  /* ==========================================================================
     04. Product page: ring resize fee
     ========================================================================== */

  // Typing a size into #custom_size adds the resize-fee variant to the cart;
  // clearing it removes the fee again (same behaviour as the Ella theme).
  var DEFAULT_FEE_VARIANT_ID = 48008993243303;

  function feeVariantId(input) {
    var wrapper = input.closest('[data-fee-variant]');
    var id = wrapper ? parseInt(wrapper.getAttribute('data-fee-variant'), 10) : NaN;
    return isNaN(id) ? DEFAULT_FEE_VARIANT_ID : id;
  }

  function getCart() {
    return fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart.js', {
      headers: { Accept: 'application/json' }
    }).then(function (res) { return res.json(); });
  }

  // Prefer Shopify standard actions so Horizon's cart drawer and count refresh.
  function updateLines(lines, fallback) {
    var actions = window.Shopify && window.Shopify.actions;
    if (actions && typeof actions.updateCart === 'function') {
      return actions.updateCart({ lines: lines }, { event: { context: 'standard-action' } });
    }
    return fallback();
  }

  function addFee(variantId) {
    return updateLines([{ merchandiseId: String(variantId), quantity: 1 }], function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
    });
  }

  function removeFee(lineKey) {
    return updateLines([{ id: lineKey, quantity: 0 }], function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
    });
  }

  var isProcessing = false;

  function onSizeInput(input) {
    var wrapper = input.closest('.sh-ring-resize');
    var active = wrapper && wrapper.querySelector('.sh-ring-resize__active');
    var hasValue = input.value.trim().length > 0;
    if (active) active.hidden = !hasValue;

    if (isProcessing) return;
    isProcessing = true;
    var variantId = feeVariantId(input);

    getCart()
      .then(function (cart) {
        var feeItem = cart.items.find(function (item) { return item.id == variantId; });
        if (hasValue && !feeItem) return addFee(variantId);
        if (!hasValue && feeItem) return removeFee(feeItem.key);
      })
      .catch(function (error) { console.error('[ring resize]', error); })
      .then(function () { isProcessing = false; });
  }

  document.addEventListener('input', function (event) {
    if (event.target && event.target.id === 'custom_size') onSizeInput(event.target);
  });

  /* ==========================================================================
     05. Product page: Tamara pay-later widgets
     ========================================================================== */

  // One loader for Tamara's script, shared by both widgets below.
  var tamaraCallbacks = [];
  function loadTamaraScript(callback) {
    if (callback) tamaraCallbacks.push(callback);
    if (window.TamaraWidget || window.__shTamaraLoaded) {
      tamaraCallbacks.splice(0).forEach(function (fn) { fn(); });
      return;
    }
    if (document.getElementById('sh-tamara-script')) return;
    var script = document.createElement('script');
    script.id = 'sh-tamara-script';
    script.src = 'https://cdn.tamara.co/widget-v2/tamara-widget.js';
    script.defer = true;
    script.onload = function () {
      window.__shTamaraLoaded = true;
      tamaraCallbacks.splice(0).forEach(function (fn) { fn(); });
    };
    document.body.appendChild(script);
  }

  // <tamara-widget> summary block (blocks/sh-tamara.liquid): settings come
  // from data attributes on the block wrapper.
  function initTamaraSummary() {
    var wrapper = document.querySelector('[data-tamara-key]');
    if (!wrapper) return;
    window.tamaraWidgetConfig = {
      lang: wrapper.dataset.tamaraLang || 'en',
      country: wrapper.dataset.tamaraCountry || 'AE',
      publicKey: wrapper.dataset.tamaraKey,
      css: ':host { --font-primary: inherit !important; --font-secondary: inherit !important; } .tamara-summary-widget__amount { font-weight: 700 !important; }',
      style: { fontSize: '14px', badgeRatio: 1.2 }
    };
    loadTamaraScript();
  }

  // Legacy #tamara-widget-container placeholder carried over from Ella.
  function initTamaraWidget() {
    var container = document.getElementById('tamara-widget-container');
    if (!container) return;

    var priceEl = document.querySelector('product-price .price, .price, [data-product-price]');
    if (!priceEl) return;
    var amount = parseFloat(priceEl.innerText.replace(/[^0-9.]/g, ''));
    if (!amount) return;

    window.tamaraConfig = {
      lang: 'en',
      country: 'AE',
      publicKey: 'd1451cef-2aea-4f57-8106-7a2a3480ab44',
      amount: amount,
      selector: '#tamara-widget-container'
    };

    loadTamaraScript(function () {
      if (window.TamaraWidget) new window.TamaraWidget(window.tamaraConfig);
    });
  }

  onReady(initTamaraSummary);
  onReady(initTamaraWidget);

  /* ==========================================================================
     06. Product page: frequently bought together bundle
     ========================================================================== */

  // Totals update as items are ticked; "Add all" adds the picked variants
  // and applies the FBT-BUNDLE-<product id> discount code when all are picked.
  define('sh-product-bundle', class extends HTMLElement {
    connectedCallback() {
      this.rate = (parseFloat(this.dataset.discountRate) || 0) / 100;
      this.addEventListener('change', () => this.update());
      this.querySelector('[data-bundle-add]').addEventListener('click', () => this.add());
      this.update();
    }
    rows() {
      return [...this.querySelectorAll('.sh-bundle__item')];
    }
    selected() {
      return this.rows().filter((row) => row.querySelector('.sh-bundle__check').checked);
    }
    format(cents) {
      return formatMoney(cents, this.dataset.moneyFormat);
    }
    priceOf(row) {
      const el = row.querySelector('[data-bundle-variant]');
      const opt = el.tagName === 'SELECT' ? el.selectedOptions[0] : el;
      return parseInt(opt.dataset.price, 10) || 0;
    }
    update() {
      const picked = this.selected();
      const total = picked.reduce((sum, row) => sum + this.priceOf(row), 0);
      const allPicked = picked.length === this.rows().length;
      const compare = this.querySelector('[data-bundle-compare]');
      const totalEl = this.querySelector('[data-bundle-total]');
      if (this.rate > 0 && allPicked) {
        compare.hidden = false;
        compare.textContent = this.format(total);
        totalEl.textContent = this.format(Math.round(total * (1 - this.rate)));
      } else {
        compare.hidden = true;
        totalEl.textContent = this.format(total);
      }
    }
    async add() {
      const button = this.querySelector('[data-bundle-add]');
      const error = this.querySelector('.sh-bundle__error');
      const picked = this.selected();
      const items = picked.map((row) => ({ id: parseInt(row.querySelector('[data-bundle-variant]').value, 10), quantity: 1 }));
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      error.hidden = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error((await res.json()).description || res.statusText);
        if (this.rate > 0 && picked.length === this.rows().length) {
          await fetch('/discount/' + encodeURIComponent('FBT-BUNDLE-' + this.dataset.productId) + '?redirect=/cart');
        }
        window.location.href = '/cart';
      } catch (e) {
        error.textContent = e.message;
        error.hidden = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  });

  /* ==========================================================================
     07. Product page: Judge.me star badge scrolls to the review widget
     ========================================================================== */

  document.addEventListener('click', function (event) {
    var badge = event.target.closest('.jdgm-prev-badge');
    var widget = document.getElementById('judgeme_product_reviews');
    if (!badge || !widget || badge.closest('a')) return;
    widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ==========================================================================
     08. Recently viewed products
     ========================================================================== */

  // Product pages record their handle (data-current); the section then
  // renders up to data-limit other products and stays hidden until it has
  // something to show.
  var RV_KEY = 'sh:recently-viewed';
  function rvRead() {
    try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
  }
  function rvWrite(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 20))); } catch (e) {}
  }

  define('sh-recently-viewed', class extends HTMLElement {
    connectedCallback() {
      var current = this.dataset.current;
      if (current) rvWrite([current].concat(rvRead().filter(function (h) { return h !== current; })));
      var limit = parseInt(this.dataset.limit, 10) || 8;
      var handles = rvRead().filter(function (h) { return h && h !== current; }).slice(0, limit);
      if (!handles.length) return;
      Promise.all(handles.map(function (h) {
        return fetch(root + 'products/' + encodeURIComponent(h) + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      })).then(this.render.bind(this));
    }
    render(products) {
      var list = this.querySelector('[data-rv-list]');
      var format = this.dataset.moneyFormat;
      products.filter(Boolean).forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'sh-rv__item';
        var img = p.featured_image ? p.featured_image.replace(/^https?:/, '') : '';
        var sep = img.indexOf('?') > -1 ? '&' : '?';
        var a = document.createElement('a');
        a.className = 'sh-rv__card';
        a.href = p.url;
        a.innerHTML =
          '<span class="sh-rv__media">' +
            (img ? '<img loading="lazy" width="600" height="750" alt="" src="' + img + sep + 'width=600" srcset="' + img + sep + 'width=300 300w, ' + img + sep + 'width=600 600w" sizes="(min-width: 990px) 25vw, 50vw">' : '') +
          '</span>' +
          '<span class="sh-rv__name"></span>' +
          '<span class="sh-rv__price">' +
            (p.compare_at_price > p.price ? '<s>' + formatMoney(p.compare_at_price, format) + '</s> ' : '') +
            formatMoney(p.price, format) +
          '</span>';
        a.querySelector('.sh-rv__name').textContent = p.title;
        var image = a.querySelector('img');
        if (image) image.alt = p.title;
        li.appendChild(a);
        list.appendChild(li);
      });
      if (list.children.length) this.hidden = false;
    }
  });

  /* ==========================================================================
     09. Blog article: reading progress, product slider, copy link
     ========================================================================== */

  onReady(function () {
    var bar = document.querySelector('[data-sh-progress]');
    var body = document.querySelector('.sh-article__content');
    if (bar && body) {
      var update = function () {
        var r = body.getBoundingClientRect();
        var total = r.height - window.innerHeight * 0.5;
        var done = Math.min(1, Math.max(0, -r.top / (total > 0 ? total : 1)));
        bar.style.transform = 'scaleX(' + done + ')';
      };
      // Horizon scrolls inside .page-wrapper on some layouts, so listen to both.
      var scroller = document.querySelector('.page-wrapper');
      window.addEventListener('scroll', update, { passive: true });
      if (scroller) scroller.addEventListener('scroll', update, { passive: true });
      update();
    }

    document.querySelectorAll('.sh-article__products').forEach(function (box) {
      var track = box.querySelector('[data-sh-mini-track]');
      if (!track) return;
      var step = function (dir) {
        var slide = track.querySelector('.sh-mini__slide');
        var amount = slide ? slide.getBoundingClientRect().width + 16 : track.clientWidth;
        track.scrollBy({ left: dir * amount, behavior: 'smooth' });
      };
      box.querySelector('[data-sh-mini-prev]').addEventListener('click', function () { step(-1); });
      box.querySelector('[data-sh-mini-next]').addEventListener('click', function () { step(1); });
    });

    document.querySelectorAll('[data-sh-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(btn.dataset.shCopy).then(function () {
          var label = btn.textContent;
          btn.textContent = 'Link copied';
          setTimeout(function () { btn.textContent = label; }, 1800);
        });
      });
    });
  });

  /* ==========================================================================
     10. Footer: menus collapse into accordions on phones
     ========================================================================== */

  onReady(function () {
    var mq = window.matchMedia('(max-width: 749px)');
    function sync() {
      document.querySelectorAll('[data-sh-footer-menu]').forEach(function (d) { d.open = !mq.matches; });
    }
    sync();
    mq.addEventListener('change', sync);
  });

  /* ==========================================================================
     11. Scrolling rails with arrow buttons
     ========================================================================== */

  // Any [data-sh-rail] wrapper with a [data-sh-rail-track] list and
  // prev/next buttons scrolls by one card per click.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-sh-rail-prev], [data-sh-rail-next]');
    if (!button) return;
    var rail = button.closest('[data-sh-rail]');
    var track = rail && rail.querySelector('[data-sh-rail-track]');
    if (!track) return;
    var card = track.firstElementChild;
    var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
    track.scrollBy({ left: button.hasAttribute('data-sh-rail-prev') ? -step : step, behavior: 'smooth' });
  });

  /* ==========================================================================
     12. Cart drawer: click the dimmed page to close it
     ========================================================================== */

  // The overlay is .page-wrapper::after, so a click on it targets the
  // wrapper itself rather than anything inside the page.
  document.addEventListener('click', function (event) {
    var wrapper = event.target;
    if (!wrapper.classList || !wrapper.classList.contains('page-wrapper--drawer-open')) return;
    var drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  /* ==========================================================================
     13. Removed product URLs (404) go to the all-products collection
     ========================================================================== */

  // On Shopify's 404 page request.path is "/404", so the requested address
  // is checked here instead. Runs as soon as this file loads.
  (function () {
    var target = document.querySelector('meta[name="sh-404-redirect"]');
    if (target && /\/products\//.test(window.location.pathname)) {
      window.location.replace(target.content || '/collections/all');
    }
  })();
})();
